/**
 * Member profile inline editing.
 *
 * Handles toggling between read and edit modes on `.member-profile-content`,
 * collecting field values via `[data-field]` attributes and submitting them
 * via AJAX to the `membri_update_compte` action.
 */

import domReady from '@wordpress/dom-ready';
import { __ } from '@wordpress/i18n';

domReady(() => {
  const container = document.querySelector('.member-profile-content');
  if (!container) return;
  const btncontainer = document.querySelector('.update-profile-block__content');
  if (!btncontainer) return;

  const btnStart = btncontainer.querySelector('.js-profile-edit-start');
  const btnSave = btncontainer.querySelector('.js-profile-edit-save');
  const btnCancel = btncontainer.querySelector('.js-profile-edit-cancel');

  if (!btnStart || !btnSave || !btnCancel) return;

  const message = container.querySelector(
    '.member-profile-content__edit-message'
  );

  // Floating toast elements (auto-dismissing confirmation).
  const toast = container.querySelector('.member-profile-content__toast');
  const toastText = toast
    ? toast.querySelector('.member-profile-content__toast-text')
    : null;
  const toastClose = toast
    ? toast.querySelector('.member-profile-content__toast-close')
    : null;
  let toastTimer = null;

  function showToast(text, type) {
    if (!toast || !toastText) return;
    if (type !== 'success' && type !== 'error') return;

    clearTimeout(toastTimer);
    toast.classList.remove('is-success', 'is-error');
    toast.removeAttribute('hidden');

    toastText.textContent = text;
    toast.classList.add('is-' + type);

    // Force reflow so the entry transition runs.
    // eslint-disable-next-line no-unused-expressions
    toast.offsetWidth;
    toast.classList.add('is-visible');

    toastTimer = setTimeout(hideToast, 5000);
  }

  /**
   * Hide the floating toast.
   */
  function hideToast() {
    if (!toast) return;
    toast.classList.remove('is-visible');
    setTimeout(() => {
      toast.setAttribute('hidden', '');
      toast.classList.remove('is-success', 'is-error');
    }, 300);
  }

  if (toastClose) {
    toastClose.addEventListener('click', () => {
      clearTimeout(toastTimer);
      hideToast();
    });
  }

  const ajaxUrl =
    (window.membriUpdateCompte || {}).ajaxUrl ||
    (window.theme || {}).ajax_url ||
    '';
  const nonce = container.dataset.nonce || '';
  const succursaleId = container.dataset.succursaleId || '';

  // Snapshot of initial display values, keyed by data-field.
  let initialDisplayValues = {};
  // Snapshot of initial input/select values (for cancel restore).
  let initialInputValues = {};

  /**
   * Collect initial state so "Cancel" can restore it.
   */
  function snapshotInitialState() {
    initialDisplayValues = {};
    initialInputValues = {};

    container.querySelectorAll('[data-field]').forEach((ctrl) => {
      const field = ctrl.dataset.field;
      if (ctrl.type === 'checkbox') {
        initialInputValues[field] = ctrl.checked;
      } else {
        initialInputValues[field] = ctrl.value;
      }

      const displayEl = getDisplayEl(ctrl);
      if (displayEl) {
        initialDisplayValues[field] = displayEl.textContent.trim();
      }
    });
  }

  /**
   * Given an edit control, find the nearest `.js-display-value` sibling.
   *
   * @param {HTMLElement} ctrl - The edit control element.
   * @return {HTMLElement|null} The matching display element, or null.
   */
  function getDisplayEl(ctrl) {
    const card = ctrl.closest('.member-profile-content-card');
    if (!card) return null;

    // For publication checkboxes the display element is the span inside the card.
    if (ctrl.closest('.member-profile-content__publication-checkboxes')) {
      return card.querySelector('.publication-info-label__info');
    }
    return card.querySelector('.js-display-value');
  }

  /**
   * Switch into edit mode.
   */
  function enterEditMode() {
    snapshotInitialState();
    container.classList.add('is-editing');
    btnStart.style.display = 'none';
    btnSave.style.display = '';
    btnCancel.style.display = '';
    setMessage('', false);
  }

  /**
   * Restore display values and exit edit mode.
   *
   * @param {boolean} restoreValues - Whether to restore initial input values.
   */
  function exitEditMode(restoreValues) {
    if (restoreValues) {
      container.querySelectorAll('[data-field]').forEach((ctrl) => {
        const field = ctrl.dataset.field;
        if (ctrl.type === 'checkbox') {
          ctrl.checked = initialInputValues[field] || false;
        } else {
          ctrl.value = initialInputValues[field] || '';
        }
      });
    }
    container.classList.remove('is-editing');
    btnStart.style.display = '';
    btnSave.style.display = 'none';
    btnCancel.style.display = 'none';
    setMessage('', false);
  }

  function setMessage(text, isError) {
    if (!message) return;
    message.textContent = text;
    message.classList.toggle('has-error', isError);
  }

  function setBusy(busy) {
    btnSave.disabled = busy;
    btnCancel.disabled = busy;
  }

  /**
   * Collect all edit-control values from the DOM.
   */
  function collectFields() {
    const fields = {};
    // For checkboxes we need both values (checked = true, absent = false).
    // Iterate inputs/selects with data-field.
    container
      .querySelectorAll('input[data-field], select[data-field]')
      .forEach((ctrl) => {
        const field = ctrl.dataset.field;
        if (ctrl.type === 'checkbox') {
          fields[field] = ctrl.checked ? '1' : '0';
        } else {
          fields[field] = ctrl.value;
        }
      });
    return fields;
  }

  /**
   * Update display elements after a successful save.
   *
   * @param {Object} responseData - The data returned from the save request.
   */
  function updateDisplay(responseData) {
    // revenue label.
    if (responseData.revenueLabel !== undefined) {
      const el = container.querySelector('.revenue.js-display-value');
      if (el) el.textContent = responseData.revenueLabel || '—';
    }

    // ownerOrRenter label.
    if (responseData.ownerOrRenterLabel !== undefined) {
      const el = container.querySelector(
        '.has-online-publication-label.js-display-value'
      );
      if (el) {
        const label = responseData.ownerOrRenterLabel || '';
        el.textContent = label || '—';
      }
    }

    // publications.
    const pubSpan = container.querySelector(
      '.publication-info-label__info.js-display-value'
    );
    if (pubSpan) {
      const parts = [];
      if (responseData.hasOnlinePublication) parts.push('Digital');
      if (responseData.hasPaperPublication) parts.push('Paper');
      pubSpan.textContent = parts.length ? parts.join(' & ') : '—';
    }

    // averageMealPricing.
    if (responseData.averageMealPricing !== undefined) {
      const el = container.querySelector(
        '.average-meal-price.js-display-value'
      );
      if (el) {
        const val = parseFloat(responseData.averageMealPricing);
        el.textContent = isNaN(val)
          ? '—'
          : val.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',') + '$';
      }
    }

    // Integer stepper fields.
    const intFields = {
      numberOfPrivateRooms: '.private-rooms.js-display-value',
      numberOfSeats: '.number-of-seats.js-display-value',
      numberOfEmployees: '.number-of-employees.js-display-value',
      numberOfOutsideSeats: '.number-of-outside-seats.js-display-value',
      numberOfDailyClients: '.number-of-daily-clients.js-display-value',
    };
    Object.entries(intFields).forEach(([field, selector]) => {
      if (responseData[field] !== undefined) {
        const el = container.querySelector(selector);
        if (el)
          el.textContent =
            responseData[field] !== null ? responseData[field] : '—';
      }
    });
  }

  /**
   * Send data via AJAX.
   */
  function saveData() {
    const fields = collectFields();
    const body = new URLSearchParams();
    body.append('action', 'membri_update_compte');
    body.append('nonce', nonce);
    body.append('succursale_id', succursaleId);
    Object.entries(fields).forEach(([k, v]) => body.append(k, v));

    setBusy(true);
    setMessage('', false);

    fetch(ajaxUrl, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
      },
      body: body.toString(),
    })
      .then((res) => res.json())
      .then((json) => {
        setBusy(false);
        if (json.success) {
          updateDisplay(json.data || {});
          exitEditMode(false);
          showToast(
            __('Your profile has been updated successfully.', 'vtx'),
            'success'
          );
        } else {
          const msg =
            json.data && json.data.message
              ? json.data.message
              : __('An error occurred. Please try again.', 'vtx');
          setMessage(msg, true);
          showToast(msg, 'error');
        }
      })
      .catch(() => {
        setBusy(false);
        const msg = __('Network error. Please try again.', 'vtx');
        setMessage(msg, true);
        showToast(msg, 'error');
      });
  }

  // Button event listeners.
  btnStart.addEventListener('click', enterEditMode);
  btnCancel.addEventListener('click', () => exitEditMode(true));
  btnSave.addEventListener('click', saveData);
});
